import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // On mount: restore user from localStorage token
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) { setLoading(false); return; }

        api.get('/me')
            .then(res => setUser(res.data))
            .catch(() => localStorage.removeItem('access_token'))
            .finally(() => setLoading(false));
    }, []);

    // Sliding session auto-refresh
    useEffect(() => {
        if (!user || user.session_timeout == null) return;
        
        const timeoutMs = user.session_timeout * 1000;
        // Refresh when 80% of the session time has passed
        const refreshMs = timeoutMs * 0.8;
        
        // Prevent 32-bit max integer overflow for setInterval (approx 24.8 days)
        const safeRefreshMs = Math.min(refreshMs, 2140000000);

        const interval = setInterval(() => {
            api.post('/auth/refresh')
                .then(res => {
                    localStorage.setItem('access_token', res.data.access_token);
                    setUser(res.data.user);
                })
                .catch(() => {
                    // If refresh fails (user deleted, permissions revoked, or token fully expired)
                    localStorage.removeItem('access_token');
                    setUser(null);
                });
        }, safeRefreshMs);

        return () => clearInterval(interval);
    }, [user]);

    const login = useCallback(async (loginStr, password) => {
        const res = await api.post('/auth/login', { login: loginStr, password });
        localStorage.setItem('access_token', res.data.access_token);
        setUser(res.data.user);
        // Backend handles login activity logging automatically
        return res.data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('access_token');
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be inside AuthProvider');
    return ctx;
}
